<template>
  <div
    class="py-4 px-4 h-100"
  >
    <div
      v-if="isPageLoading"
      class="loader mx-auto h-100"
    />
    <template
      v-else
    >
      <section
        class="mb-3"
      >
        <b-row>
          <b-col>
            <base-help-tooltip
              popover-placement="bottom"
            >
              <template v-slot:left>
                <div>
                  <h1 class="page-title">
                    Creative Performance
                  </h1>
                </div>
              </template>
              <template v-slot:popover-body>
                <p
                  class="mb-0"
                >
                  View the creative performance including the engagement data.
                </p>
              </template>
            </base-help-tooltip>
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row class="mt-6">
          <b-col>
            <ReportStatements
              :type="'CAMPAIGN_PERFORMANCE'"
            />
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row
          class="mb-2"
        >
          <b-col class="text-right">
            <b-btn
              variant="primary"
              class="ml-2 border-0 justify-content-right"
              data-test="export-creative-data-csv-button"
              @click="downloadAsCSV()"
              :disabled="!overallCreativeCampaignData"
            >
              Export all creative data as CSV
            </b-btn>
          </b-col>
        </b-row>
        <b-row
          align-h="between"
          class="mb-3"
        >
          <b-col
            cols="auto"
          >
            <base-filter-dropdown
              :filter="'publishers'"
              :options-list="campaignPublishersList"
              :key-property="'id'"
              :value-property="'name'"
              :selected-options="selectedPublishersIds"
              @updatedSelectedOptions="updatedSelectedOptions('selectedPublishersIds', ...arguments)"
              @selectAllOptions="selectAllOptions('campaignPublishersList', 'selectedPublishersIds', 'id')"
              @deselectAllOptions="deselectAllOptions('selectedPublishersIds')"
            />
            <base-filter-dropdown
              class="ml-3"
              :filter="'creative'"
              :options-list="GET_ACTIVE_CREATIVES"
              :key-property="'id'"
              :value-property="'name'"
              :selected-options="selectedCreativesIds"
              @updatedSelectedOptions="updatedSelectedOptions('selectedCreativesIds', ...arguments)"
              @selectAllOptions="selectAllOptions('GET_ACTIVE_CREATIVES', 'selectedCreativesIds', 'id')"
              @deselectAllOptions="deselectAllOptions('selectedCreativesIds')"
            />
          </b-col>
          <b-col
            cols="auto"
          >
            <date-picker-dropdown
              @updateDates="updateDates"
              right
              toggle-class="text-right"
            />
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row>
          <b-col>
            <b-card
              no-body
            >
              <b-card-body
                class="pb-0"
                style="min-height: calc(28rem);"
              >
                <div
                  v-if="!eventPerformanceChartData.loaded"
                  class="loader mx-auto my-5"
                />
                <event-performance-chart
                  v-else
                  :data="eventPerformanceChartData"
                />
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row>
          <b-col
            cols="12"
          >
            <b-card
              class="mb-5"
            >
              <div class="table-responsive">
                <b-table
                  class="card-table table-vcenter text-nowrap"
                  tbody-tr-class="creative-performance-table-row"
                  :striped="true"
                  :hover="true"
                  :items="overallCreativeCampaignData"
                  :fields="returnHeaders"
                  :busy="isCampaignPerformanceTableBusy"
                  show-empty
                  data-test="creative-performance-table"
                  sort-by="name"
                >
                  <template
                    v-slot:cell(creative)="{item}"
                  >
                    {{ item.sizeName }}
                  </template>
                  <template
                    v-slot:cell(impressions)="{item}"
                  >
                    {{ toLocaleString(item.impressions) }}
                  </template>
                  <template
                    v-slot:cell(clicks)="{item}"
                  >
                    {{ toLocaleString(item.clicks) }}
                  </template>
                  <template
                    v-slot:cell(ctr)="{item}"
                  >
                    {{ toLocaleString(item.ctr, true) + "%" }}
                  </template>
                  <template
                    v-slot:cell(scrolls)="{item}"
                  >
                    {{ isValidNumber(item.scrolls) ? toLocaleString(item.scrolls) : "N/A" }}
                  </template>
                  <template
                    v-slot:cell(str)="{item}"
                  >
                    {{ isValidNumber(item.str) ? toLocaleString(item.str, true) + "%" : "N/A" }}
                  </template>
                  <template
                    #table-busy
                    style="min-height: calc(16rem);"
                  >
                    <div class="loader mx-auto" />
                  </template>
                </b-table>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </section>
    </template>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';

import helper from '@/core/helper';
import BaseFilterDropdown from '@/components/BaseFilterDropdown.vue';
import DatePickerDropdown from '@/components/DatePickerDropdown.vue';
import EventPerformanceChart from '@/modules/reporting/components/CreativePerformance/EventPerformanceChart.vue';
import ReportStatements from '@/modules/reporting/components/ReportStatements.vue';
import numbersHelper from '@/core/mixins/numbersHelper';
import dateHelper from '@/core/mixins/dateHelper';
import fileHelper from '@/core/mixins/fileHelper';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';

export default {
  name: 'CreativePerformance',
  components: {
    BaseHelpTooltip,
    BaseFilterDropdown,
    DatePickerDropdown,
    EventPerformanceChart,
    ReportStatements,
  },
  mixins: [
    numbersHelper,
    dateHelper,
    fileHelper,
  ],
  data() {
    return {
      isPageLoading: true,
      campaignPublishersList: [],
      selectedPublishersIds: [],
      selectedCreativesIds: [],
      dateRange: {
        start: -1,
        end: -1,
      },
      dailyStats: [],
      eventPerformanceChartData: {
        loaded: false,
        labels: [],
        clicks: [],
        ctr: [],
        scrolls: [],
        str: [],
      },
      overallCreativeCampaignData: [],
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ACTIVE_CREATIVES,
    ]),
    hasScrollsData() {
      return this.dailyStats.some((stats) => stats.scrolls != null);
    },
    returnHeaders() {
      const headersWithScroll = [
        'creative',
        'impressions',
        'clicks',
        { key: 'ctr', label: 'ctr%' },
        'scrolls',
        { key: 'str', label: 'str%' },
      ];
      const headersWithNoScroll = [
        'creative',
        'impressions',
        'clicks',
        { key: 'ctr', label: 'ctr%' },
      ];
      return this.hasScrollsData ? headersWithScroll : headersWithNoScroll;
    },
    isCampaignPerformanceTableBusy() {
      return !this.overallCreativeCampaignData.lengh
        && (!this.eventPerformanceChartData.loaded);
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_PUBLISHERS,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_EXISTING_CREATIVES_AS_SHAREE,
    ]),
    ...mapActions('reporting', [
      reportingTypes.FETCH_CAMPAIGN_CONTENT_DAILY_STATS,
      reportingTypes.FETCH_CAMPAIGN_PUBLISHER_DAILY_STATS,
      reportingTypes.FETCH_PUBLISHER_IDS_WITH_STATS,
      reportingTypes.FETCH_OVERALL_CREATIVE_CAMPAIGN_STATS,
      reportingTypes.DOWNLOAD_CREATIVE_REPORT_CSV,
    ]),
    updatedSelectedOptions(selectedOptionsList, id, isChecked) {
      isChecked ? this[selectedOptionsList].push(id)
        : this[selectedOptionsList].splice(this[selectedOptionsList].indexOf(id), 1);
      this.updateReport();
    },
    selectAllOptions(fullList, selectedOptionsList, keyProperty) {
      this[selectedOptionsList] = this[fullList].map((option) => option[keyProperty]);
      this.updateReport();
    },
    deselectAllOptions(selectedOptionsList) {
      this[selectedOptionsList] = [];
    },
    updateDates(startDate, endDate) {
      this.dateRange.start = startDate;
      this.dateRange.end = endDate;
      this.updateReport();
    },
    updateReport: helper.debounce(function fetchData() {
      if (this.selectedPublishersIds.length > 0 && this.selectedCreativesIds.length > 0) {
        this.fetchPublisherData();
        this.fetchOverallCreativeCampaignData();
      }
    }, 1500),
    resetEventPerformanceChart() {
      this.eventPerformanceChartData = {
        loaded: false,
        labels: [],
        clicks: [],
        ctr: [],
        scrolls: [],
        str: [],
      };
    },
    populateEventPerformanceChartData(stats) {
      for (let i = 0; i < stats.length; i++) {
        this.eventPerformanceChartData.labels.push(this.formatDate(stats[i].date, 'dd/MM'));
        this.eventPerformanceChartData.clicks.push(stats[i].clicks);
        this.eventPerformanceChartData.ctr.push(stats[i].ctr);
        if (this.hasScrollsData) {
          this.eventPerformanceChartData.scrolls.push(stats[i].scrolls);
          this.eventPerformanceChartData.str.push(stats[i].str);
        }
      }
    },
    fetchPublisherData() {
      this.dailyStats = [];
      this.resetEventPerformanceChart();
      const payload = {
        advertiserId: this.campaign.advertiserId,
        campaignId: this.campaign.id,
        startDate: this.dateRange.start === -1 ? this.order.startDate : this.dateRange.start,
        endDate: this.dateRange.end === -1 ? this.order.endDate : this.dateRange.end,
        publisherIds: this.selectedPublishersIds.join(','),
        creativeIds: this.selectedCreativesIds.join(','),
      };
      this.FETCH_CAMPAIGN_PUBLISHER_DAILY_STATS(payload)
        .then((response) => {
          this.dailyStats = response.dailyStats;
          this.populateEventPerformanceChartData(response.dailyStats);
          this.eventPerformanceChartData.loaded = true;
        });
    },
    fetchPublishers() {
      const payload = {
        all: true,
      };
      return this.FETCH_PUBLISHERS(payload);
    },
    fetchPublisherIdsWithStats() {
      const payload = {
        creativeIds: this.selectedCreativesIds.join(','),
      };
      return this.FETCH_PUBLISHER_IDS_WITH_STATS(payload);
    },
    createPublisherList(fullPublishersList, publishersIdsWithStats) {
      this.campaignPublishersList = fullPublishersList.flat()
        .filter((publisher) => publishersIdsWithStats.includes(publisher.id));
    },
    fetchOverallCreativeCampaignData() {
      const { advertiserId, campaignId } = this.$route.params;
      const payload = {
        advertiserId,
        campaignId,
        startDate: this.dateRange.start === -1 ? this.order.startDate : this.dateRange.start,
        endDate: this.dateRange.end === -1 ? this.order.endDate : this.dateRange.end,
        publisherIds: this.selectedPublishersIds.join(','),
        creativeIds: this.selectedCreativesIds.join(','),
      };
      this.FETCH_OVERALL_CREATIVE_CAMPAIGN_STATS(payload)
        .then((response) => {
          this.overallCreativeCampaignData.splice(0, this.overallCreativeCampaignData.length, ...response);
        });
    },
    async downloadAsCSV() {
      const { advertiserId, campaignId } = this.$route.params;
      const payload = {
        advertiserId,
        campaignId,
        startDate: this.dateRange.start === -1 ? this.order.startDate : this.dateRange.start,
        endDate: this.dateRange.end === -1 ? this.order.endDate : this.dateRange.end,
        publisherIds: this.selectedPublishersIds.join(','),
        creativeIds: this.selectedCreativesIds.join(','),
        reportType: 'CAMPAIGN_PERFORMANCE',
      };
      const csvContent = await this.DOWNLOAD_CREATIVE_REPORT_CSV(payload);
      this.downloadCsvFile(csvContent, `${this.campaign.name} - Creative Report`);
    },
    handleCreate() {
      try {
        this.selectAllOptions('GET_ACTIVE_CREATIVES', 'selectedCreativesIds', 'id');
        const fetchPublishers = this.fetchPublishers();
        const fetchPublisherIdsWithStats = this.fetchPublisherIdsWithStats();
        Promise.all([fetchPublishers, fetchPublisherIdsWithStats])
          .then(([fullPublishersList, publishersIdsWithStats]) => {
            this.createPublisherList(fullPublishersList, publishersIdsWithStats);
            this.selectAllOptions('campaignPublishersList', 'selectedPublishersIds', 'id');
            this.isPageLoading = false;
          });
      } catch (e) {
        this.isPageLoading = false;
        this.$router.back();
      }
    },
  },
  async created() {
    await this.FETCH_EXISTING_CREATIVES_AS_SHAREE(this.campaign.id);
    this.handleCreate();
  },
};
</script>
<style scoped lang="scss">
::v-deep {
  .card-table thead tr th:not(:last-child),
  .card-table tbody tr td:not(:last-child) {
    border-right: 1px solid rgb(222, 226, 230);
  }
}
</style>
